import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePlatformPermissions } from '@/hooks/usePlatformPermissions';
import { requiredPlatformPermissionForPath } from '@/lib/platformPermissions';

// ─────────────────────────────────────────────────────────────
// Garde d'accès des routes du SuperAdmin.
// Un travailleur plateforme limité ne peut pas ouvrir une page dont il
// n'a pas la permission : on affiche un écran « accès refusé ».
// ─────────────────────────────────────────────────────────────

export default function PlatformPermissionGuard({ children }: { children: ReactNode }) {
  const { ready, has } = usePlatformPermissions();
  const { pathname } = useLocation();

  if (!ready) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  const required = requiredPlatformPermissionForPath(pathname);

  if (!has(required)) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-2-line text-3xl text-accent-700"></i>
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Accès non autorisé</h2>
        <p className="text-sm text-foreground-500 mt-2 max-w-md">
          Votre profil ne vous donne pas accès à cette section du back-office. Contactez le
          superadmin principal pour obtenir la permission correspondante.
        </p>
        <Link
          to="/superadmin"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Retour au tableau de bord
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}